/**
 * Call History — the Analytics view.
 *
 * The table lists the calls; this shows the shape of them: when they arrive,
 * how many connect, how long they run, who handles them and how they end.
 *
 * The trend is drawn on the report's own `bucket` column — the interval chosen
 * in the drawer — so the chart and the table agree on what a period is.
 */
import { useMemo, type JSX } from 'react'
import { Chart } from 'primereact/chart'
import {
  CalendarClock, Clock, PhoneCall, PhoneOff, Layers, Users, Timer, Radio,
} from 'lucide-react'

import {
  AnalyticsCanvas,
  BarList,
  CardEmpty,
  CardGrid,
  ChartCard,
  Heatmap,
  HeroBand,
  KpiRow,
  Meter,
  SectionLabel,
  StatTile,
  type BarListItem,
} from '../analytics/AnalyticsKit.js'
import {
  VIZ,
  WEEKDAYS,
  durationBuckets,
  formatCount,
  formatPct,
  formatSeconds,
  hourDayMatrix,
  hourLabel,
  lineDataset,
  lineOptions,
  medianOf,
  ratePct,
  tally,
} from '../analytics/viz.js'
import {
  avgCol,
  bucketLabel,
  bucketSeries,
  num,
  peakOf,
  sumCol,
  text,
} from './analytics-helpers.js'

type Row = Record<string, unknown>

/** A wait this long is called out as a service problem rather than a number. */
const LONG_WAIT_SEC = 60

export default function CallHistoryAnalytics({ rows }: { rows: readonly Row[] }): JSX.Element {
  const series = useMemo(() => bucketSeries(rows, 'bucket'), [rows])
  const busiest = useMemo(() => peakOf(series), [series])
  const heat = useMemo(() => hourDayMatrix(rows, 'startTime'), [rows])

  const trendData = useMemo(
    () => ({
      labels: series.labels,
      datasets: [lineDataset('Calls', series.values, VIZ.series[0], { fill: true })],
    }),
    [series],
  )

  const totals = useMemo(() => {
    let answered = 0
    let abandoned = 0
    let inbound = 0
    let outbound = 0
    let recorded = 0
    let longWaits = 0
    for (const row of rows) {
      if (text(row['callStatus']) === 'Answered') answered += 1
      if (row['abandoned'] === true) abandoned += 1
      const direction = text(row['direction'])
      if (direction === 'inbound') inbound += 1
      else if (direction === 'outbound') outbound += 1
      if (row['hasRecording'] === true) recorded += 1
      const wait = num(row['queueWaitSec'])
      if (wait !== null && wait >= LONG_WAIT_SEC) longWaits += 1
    }
    return { answered, abandoned, inbound, outbound, recorded, longWaits }
  }, [rows])

  // Averages ignore rows where the value is absent — see analytics-helpers.
  // An unanswered call has no talk time, and averaging it as zero would drag
  // the figure down by exactly the calls it does not describe.
  const avgTalk = useMemo(() => avgCol(rows, 'talkSec'), [rows])
  const avgHandle = useMemo(() => avgCol(rows, 'handleSec'), [rows])
  const avgWait = useMemo(() => avgCol(rows, 'queueWaitSec'), [rows])
  const medianDuration = useMemo(
    () => medianOf(rows.map((r) => num(r['durationSec']))),
    [rows],
  )
  const totalTalk = useMemo(() => sumCol(rows, 'talkSec'), [rows])

  const durationSpread = useMemo(
    () => durationBuckets(rows.map((r) => num(r['durationSec']))),
    [rows],
  )

  const toItems = (key: string, limit = 8): BarListItem[] =>
    tally(rows, key, { limit, emptyLabel: 'Not recorded' }).map((slice) => ({
      label: slice.label,
      value: slice.value,
      meta: formatPct(ratePct(slice.value, rows.length), 1),
    }))

  const agents = useMemo(() => toItems('agentName'), [rows])
  const queues = useMemo(() => toItems('queueName'), [rows])
  const hangupCauses = useMemo(() => toItems('hangupCause'), [rows])
  const dispositions = useMemo(() => toItems('dispositionName'), [rows])
  const dids = useMemo(() => toItems('did'), [rows])

  if (rows.length === 0) {
    return (
      <AnalyticsCanvas>
        <div className="rounded-[4px] border border-slate-200 bg-white px-6 py-16 text-center">
          <p className="text-sm font-semibold text-slate-900">Nothing to analyse</p>
          <p className="mt-1 text-xs text-slate-500">
            No call was placed or received in this window. Widen the range, or clear the filters,
            then run the report again.
          </p>
        </div>
      </AnalyticsCanvas>
    )
  }

  const peak = heat.peak
  const answerRate = ratePct(totals.answered, rows.length)

  return (
    <AnalyticsCanvas>
      <HeroBand
        eyebrow="Calls in scope"
        value={formatCount(rows.length)}
        caption={`${formatCount(totals.inbound)} inbound · ${formatCount(totals.outbound)} outbound in the selected window`}
        spark={series.values}
        Icon={PhoneCall}
        facts={[
          { label: 'Answered', value: formatPct(answerRate, 1), tone: (answerRate ?? 0) >= 90 ? 'good' : 'warning' },
          { label: 'Total talk time', value: formatSeconds(totalTalk) },
          { label: 'Average handle time', value: formatSeconds(avgHandle) },
          {
            label: 'Abandoned',
            value: formatCount(totals.abandoned),
            tone: totals.abandoned > 0 ? 'warning' : 'good',
          },
        ]}
      />

      <KpiRow>
        <StatTile label="Answered" value={formatCount(totals.answered)} hint={formatPct(answerRate, 1)} />
        <StatTile
          label="Not answered"
          value={formatCount(rows.length - totals.answered)}
          tone={rows.length - totals.answered > 0 ? 'warning' : 'good'}
        />
        <StatTile label="Average talk time" value={formatSeconds(avgTalk)} hint="Answered calls only" />
        <StatTile label="Median duration" value={formatSeconds(medianDuration)} hint="Half of calls are shorter" />
        <StatTile
          label="Average queue wait"
          value={formatSeconds(avgWait)}
          tone={(avgWait ?? 0) >= LONG_WAIT_SEC ? 'critical' : 'default'}
        />
        <StatTile
          label={`Waited over ${LONG_WAIT_SEC}s`}
          value={formatCount(totals.longWaits)}
          tone={totals.longWaits > 0 ? 'warning' : 'good'}
          hint={formatPct(ratePct(totals.longWaits, rows.length), 1)}
        />
      </KpiRow>

      {/* ── When ── */}
      <section className="flex flex-col gap-3">
        <SectionLabel note={`${series.labels.length} interval${series.labels.length === 1 ? '' : 's'}`}>
          When calls happen
        </SectionLabel>
        <CardGrid>
          <ChartCard
            title="Calls per interval"
            subtitle="On the interval chosen in the filters"
            Icon={CalendarClock}
            wide
            height={280}
            footnote={
              busiest
                ? `Busiest interval: ${bucketLabel(busiest.label)} with ${formatCount(busiest.value)} calls.`
                : undefined
            }
          >
            {series.labels.length === 0 ? (
              <CardEmpty message="No call in this result set carries an interval." />
            ) : (
              <Chart
                type="line"
                data={trendData}
                options={lineOptions({ tickLabel: bucketLabel })}
                className="h-full w-full"
              />
            )}
          </ChartCard>

          <ChartCard
            title="When calls arrive"
            subtitle="Weekday by hour of day, in your local time"
            Icon={Clock}
            wide
            height={280}
            footnote={
              peak
                ? `Peak hour: ${WEEKDAYS[peak.day]} at ${hourLabel(peak.hour)} — ${formatCount(peak.value)} calls.`
                : undefined
            }
          >
            <Heatmap matrix={heat.matrix} max={heat.max} />
          </ChartCard>
        </CardGrid>
      </section>

      {/* ── How they went ── */}
      <section className="flex flex-col gap-3">
        <SectionLabel>How calls went</SectionLabel>
        <CardGrid>
          <ChartCard title="Direction" subtitle="Inbound against outbound" height={220} Icon={Radio}>
            <Meter
              segments={[
                { label: 'Inbound', value: totals.inbound, color: VIZ.series[0] },
                { label: 'Outbound', value: totals.outbound, color: VIZ.series[1] },
                {
                  label: 'Internal',
                  value: rows.length - totals.inbound - totals.outbound,
                  color: VIZ.series[2],
                },
              ]}
              emptyMessage="No direction recorded on these calls."
            />
          </ChartCard>

          <ChartCard title="Call length" subtitle="How long calls actually run" Icon={Timer}>
            <BarList
              items={durationSpread.map((b) => ({
                label: b.label,
                value: b.value,
                meta: formatPct(ratePct(b.value, rows.length), 1),
              }))}
              format={formatCount}
              emptyMessage="No call in this window carries a duration."
            />
          </ChartCard>

          <ChartCard title="How calls end" subtitle="Hangup cause, tail folded into “Other”" Icon={PhoneOff}>
            <BarList items={hangupCauses} format={formatCount} emptyMessage="No hangup cause recorded." />
          </ChartCard>

          <ChartCard title="Dispositions" subtitle="What agents recorded afterwards" Icon={Layers}>
            <BarList
              items={dispositions}
              format={formatCount}
              emptyMessage="No disposition was set on these calls."
            />
          </ChartCard>
        </CardGrid>
      </section>

      {/* ── Who and where ── */}
      <section className="flex flex-col gap-3">
        <SectionLabel>Who handled them, and where they came in</SectionLabel>
        <CardGrid>
          <ChartCard title="Busiest agents" subtitle="Calls handled" Icon={Users}>
            <BarList items={agents} format={formatCount} emptyMessage="No call in this window reached an agent." />
          </ChartCard>
          <ChartCard title="Busiest queues" subtitle="Where calls were routed" Icon={Layers}>
            <BarList items={queues} format={formatCount} emptyMessage="No call in this window entered a queue." />
          </ChartCard>
          <ChartCard title="Numbers dialled in on" subtitle="Inbound numbers" Icon={PhoneCall}>
            <BarList items={dids} format={formatCount} emptyMessage="No inbound call in this window." />
          </ChartCard>
          <ChartCard title="Recording coverage" subtitle="Calls with a stored recording" height={220}>
            <Meter
              segments={[
                { label: 'Recorded', value: totals.recorded, color: VIZ.series[0] },
                // Neutral, not a fourth category hue: "not recorded" is the
                // absence of the thing being measured, and the palette has
                // exactly three categorical slots on purpose.
                { label: 'Not recorded', value: rows.length - totals.recorded, color: VIZ.muted },
              ]}
              emptyMessage="No call in this window."
            />
          </ChartCard>
        </CardGrid>
      </section>
    </AnalyticsCanvas>
  )
}
